import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../components/ConfirmDialog';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Plus, ArrowLeft, Eye, Trash2, Edit3, Send, Mail, ExternalLink, Sparkles, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = ['dj', 'venue', 'catering', 'food_truck', 'photography', 'videography', 'florist', 'av_lighting', 'decor', 'transportation', 'entertainment', 'rentals', 'staff', 'other'];

export default function AdminPartnerInvitations() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    category: 'dj', partner_business_name: '', partner_contact_name: '',
    partner_email: '', partner_phone: '', partner_city: '', partner_website: '',
  });
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/partner-invitations`, { headers });
      setItems(res.data.invitations || []);
    } catch (e) { toast.error('Failed to load invitations'); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.partner_business_name && !form.partner_contact_name) {
      toast.error('At least a business name or contact name'); return;
    }
    try {
      const res = await axios.post(`${API}/partner-invitations`, form, { headers });
      toast.success('Invitation drafted');
      setShowNew(false);
      navigate(`/admin/partner-invitations/${res.data.invitation_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const remove = async (item) => {
    const ok = await confirm({
      title: 'Delete this invitation?',
      body: item.partner_business_name
        ? `${item.partner_business_name} will be permanently removed. This cannot be undone.`
        : 'This invitation will be permanently removed. This cannot be undone.',
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/partner-invitations/${item.invitation_id}`, { headers });
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const send = async (item) => {
    try {
      await axios.post(`${API}/partner-invitations/${item.invitation_id}/send`, {}, { headers });
      toast.success(`Invitation emailed to ${item.partner_email}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
  };

  const statusBadge = (s) => {
    const m = {
      draft: 'bg-slate-500/15 text-slate-300',
      sent: 'bg-indigo-500/15 text-indigo-300',
      viewed: 'bg-amber-500/15 text-amber-300',
      approved: 'bg-emerald-500/15 text-emerald-300',
      onboarded: 'bg-fuchsia-500/15 text-fuchsia-300',
    };
    return <Badge className={m[s] || m.draft} variant="outline">{s}</Badge>;
  };

  return (
    <div className="min-h-screen bg-background py-10 px-4 sm:px-6 lg:px-8" data-testid="admin-partner-invitations-page">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate('/admin')} className="flex items-center text-muted-foreground hover:text-foreground mb-6 text-sm">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Admin
        </button>

        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-2">Partner Invitations</h1>
            <p className="text-muted-foreground">Recruit DJs, venues, caterers, and other suppliers with a personalized proposal + auto-onboarding.</p>
          </div>
          <Button onClick={() => setShowNew(true)} className="bg-gradient-to-r from-indigo-600 to-fuchsia-600" data-testid="new-invitation-btn">
            <Plus className="w-4 h-4 mr-2" /> New Invitation
          </Button>
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground py-16">Loading…</div>
        ) : items.length === 0 ? (
          <Card><CardContent className="py-16 text-center text-muted-foreground">
            <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>No invitations yet. Create one to start filling your supplier hub.</p>
          </CardContent></Card>
        ) : (
          <div className="grid gap-4">
            {items.map(it => (
              <Card key={it.invitation_id} data-testid={`invitation-row-${it.invitation_id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <div className="font-semibold truncate">{it.partner_business_name || it.partner_contact_name || 'Unnamed'}</div>
                        <Badge variant="outline" className="capitalize">{it.category}</Badge>
                        {statusBadge(it.status)}
                      </div>
                      <div className="text-sm text-muted-foreground">{it.partner_contact_name && <span>{it.partner_contact_name} · </span>}{it.partner_email}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {it.listings?.length || 0} draft listing{(it.listings?.length || 0) !== 1 ? 's' : ''} · viewed {it.view_count || 0}× · created {new Date(it.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/admin/partner-invitations/${it.invitation_id}`)} data-testid={`edit-${it.invitation_id}`}>
                        <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                      {it.status === 'draft' || it.status === 'sent' || it.status === 'viewed' ? (
                        <Button size="sm" variant="outline" onClick={() => send(it)} data-testid={`send-${it.invitation_id}`}>
                          <Send className="w-3.5 h-3.5 mr-1" /> {it.status === 'draft' ? 'Send' : 'Resend'}
                        </Button>
                      ) : null}
                      {it.token && (
                        <Button size="sm" variant="ghost" asChild>
                          <a href={`/partner-invitation/${it.token}`} target="_blank" rel="noreferrer"><ExternalLink className="w-3.5 h-3.5" /></a>
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(it)} data-testid={`delete-invitation-${it.invitation_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>New Partner Invitation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Partner Category</label>
              <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace('_', ' / ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <Input placeholder="Business name" value={form.partner_business_name} onChange={(e) => setForm(f => ({ ...f, partner_business_name: e.target.value }))} data-testid="new-biz-name" />
              <Input placeholder="Contact name" value={form.partner_contact_name} onChange={(e) => setForm(f => ({ ...f, partner_contact_name: e.target.value }))} />
              <Input type="email" placeholder="Email" value={form.partner_email} onChange={(e) => setForm(f => ({ ...f, partner_email: e.target.value }))} data-testid="new-email" />
              <Input placeholder="Phone" value={form.partner_phone} onChange={(e) => setForm(f => ({ ...f, partner_phone: e.target.value }))} />
              <Input placeholder="City" value={form.partner_city} onChange={(e) => setForm(f => ({ ...f, partner_city: e.target.value }))} />
              <Input placeholder="Website" value={form.partner_website} onChange={(e) => setForm(f => ({ ...f, partner_website: e.target.value }))} />
            </div>
            <p className="text-xs text-muted-foreground">Tip: pre-fill as much as you know — the partner just verifies on their end.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button onClick={create} className="bg-indigo-600" data-testid="create-invitation-btn">Create &amp; Edit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
